import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Send, ShieldCheck } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { trackQuoteRequest } from '../../lib/tracking';
import { SafeForm } from '../SafeForm';

const Field = ({ id, label, children }) => (
  <div className="space-y-1.5"><Label htmlFor={id} className="text-sm">{label}</Label>{children}</div>
);

const LeadForm = ({ submitLead, favorite, hasDesign }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', city: '', postalCode: '', message: '', budget: 'onbekend', timeline: 'onbekend' });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const lock = useRef(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async () => {
    if (lock.current) return;
    lock.current = true;
    setSending(true); setError('');
    try {
      const result = await submitLead({ ...form, favorite_generation_id: favorite });
      trackQuoteRequest('renovatie_ontwerpen', result?.lead_id);
      navigate('/bedankt');
    } catch (err) { setError(err.message); setSending(false); lock.current = false; }
  };

  return (
    <SafeForm id="visualizer-lead-form" onValidSubmit={onSubmit} className="bg-[#3a190b] text-white rounded-2xl p-6 md:p-10 space-y-5 scroll-mt-28" data-testid="visualizer-lead-form">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-2">Stap 3 — Van ontwerp naar realiteit</p>
        <h2 className="text-2xl md:text-3xl font-bold">Dit ontwerp echt laten maken?</h2>
        <p className="text-white/75 text-sm mt-2">{hasDesign ? 'Uw foto, ontwerpen en keuzes worden automatisch bij uw aanvraag gevoegd én u ontvangt ze meteen per e-mail.' : 'Ook zonder afgewerkt ontwerp kunt u een gratis plaatsbezoek aanvragen.'} Onze specialisten werken uw ontwerp vrijblijvend verder uit en bellen u binnen 24 uur voor een gratis plaatsbezoek met concrete prijs.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="vl-first" label="Voornaam *"><Input id="vl-first" required value={form.firstName} onChange={set('firstName')} className="bg-white text-[#202020]" data-testid="lead-firstname" /></Field>
        <Field id="vl-last" label="Naam *"><Input id="vl-last" required value={form.lastName} onChange={set('lastName')} className="bg-white text-[#202020]" data-testid="lead-lastname" /></Field>
        <Field id="vl-email" label="E-mail *"><Input id="vl-email" type="email" required value={form.email} onChange={set('email')} className="bg-white text-[#202020]" data-testid="lead-email" /></Field>
        <Field id="vl-phone" label="Telefoon *"><Input id="vl-phone" type="tel" required value={form.phone} onChange={set('phone')} className="bg-white text-[#202020]" data-testid="lead-phone" /></Field>
        <Field id="vl-city" label="Gemeente *"><Input id="vl-city" required value={form.city} onChange={set('city')} className="bg-white text-[#202020]" data-testid="lead-city" /></Field>
        <Field id="vl-postal" label="Postcode"><Input id="vl-postal" value={form.postalCode} onChange={set('postalCode')} className="bg-white text-[#202020]" data-testid="lead-postal" /></Field>
        <Field id="vl-budget" label="Budget">
          <select id="vl-budget" value={form.budget} onChange={set('budget')} className="w-full h-10 rounded-md bg-white text-[#202020] px-3 text-sm" data-testid="lead-budget">
            <option value="onbekend">Nog geen idee</option><option value="<15000">Minder dan €15.000</option><option value="15000-30000">€15.000 – €30.000</option><option value="30000-60000">€30.000 – €60.000</option><option value="60000+">Meer dan €60.000</option>
          </select>
        </Field>
        <Field id="vl-timeline" label="Wanneer starten?">
          <select id="vl-timeline" value={form.timeline} onChange={set('timeline')} className="w-full h-10 rounded-md bg-white text-[#202020] px-3 text-sm" data-testid="lead-timeline">
            <option value="onbekend">Nog niet beslist</option><option value="zo snel mogelijk">Zo snel mogelijk</option><option value="1-3 maanden">Binnen 1 – 3 maanden</option><option value="3-6 maanden">Binnen 3 – 6 maanden</option>
          </select>
        </Field>
      </div>
      <Field id="vl-msg" label="Opmerkingen (optioneel)"><textarea id="vl-msg" rows={2} value={form.message} onChange={set('message')} className="w-full rounded-md bg-white text-[#202020] p-3 text-sm" data-testid="lead-message" /></Field>
      {error && <p className="text-sm text-red-200" data-testid="lead-error">{error}</p>}
      <Button type="button" data-submit="true" disabled={sending} className="w-full sm:w-auto bg-white text-[#3a190b] hover:bg-gray-100 px-8 py-3 font-semibold" data-testid="lead-submit-btn">
        {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}Gratis plaatsbezoek aanvragen
      </Button>
      <p className="text-xs text-white/55 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" />Vrijblijvend. Uw gegevens en foto's worden enkel gebruikt voor deze aanvraag.</p>
    </SafeForm>
  );
};

export default LeadForm;
